import React, { useContext, useEffect, useState } from 'react'
import { Button, Col, Container, Inline, Modal, Row } from '@airbus/components-react/components'
import { useTranslation } from 'react-i18next'
import PageHeader from 'components/pageHeader'
import { DisplayModus } from 'constants/constants'
import { RequiredToolInput } from 'types/workorder/requiredTool'
import useMutation from 'utils/hooks/useMutation'
import URL_APIS from 'utils/api/urlApis'
import { FormProvider, useForm } from 'react-hook-form'
import FormInput from 'components/FormFields/FormInput'
import useOptions from 'utils/hooks/useOptions'
import FormSelect from 'components/FormFields/FormSelect'
import ModalConfirm from 'components/Modal/ModalConfim'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import ModalPn from 'components/Modal/modalPn'
import { PnSearch } from 'types/pn'
import { Lang } from 'context/LangContext'
import { DevTool } from '@hookform/devtools'
import LightDarkContext from 'context/LightDarkModeContext'
import FormNumericInput from 'components/FormFields/FormNumericInput'

type Props = {
  lang: Lang
  open: boolean
  displayMode: DisplayModus
  onClose: () => void
  resource?: RequiredToolInput
  onPostSubmit: () => void
  oenumdoc: string
}

const styleModal = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  border: '2px solid #000',
  boxShadow: 24,
  height: '400px',
  width: '60%',
  overflow: 'hidden',
}

const ModalRequiredTools = ({ lang, open, displayMode, onClose, resource, onPostSubmit, oenumdoc }: Props) => {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [modalPnOpened, setModalPnOpened] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { mutate: createOrUpdateRequiredTool } = useMutation<RequiredToolInput>(
    URL_APIS.URL_WORKORDER_REQUIRED_TOOLS_CU,
    t('9500189').toString(),
  )

  const { loadOptions, options: typeOptions } = useOptions(URL_APIS.URL_WORKORDER_REQUIRED_TOOLS_INGREDIENT_TYPES_GET)

  const methods = useForm<RequiredToolInput>({
    values: resource,
  })

  const {
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    resetField,
  } = methods

  const onSubmit = async (data: RequiredToolInput) => {
    setIsLoading(true)

    try {
      await createOrUpdateRequiredTool({
        data: { ...resource, ...data, pn: data.pn.toUpperCase(), quantite: Number(data.quantite) },
        params: displayMode === DisplayModus.CREATE ? { lang } : { lang, oenumdoc },
        method: displayMode === DisplayModus.CREATE ? 'POST' : 'PUT',
      })
    } finally {
      setIsLoading(false)
    }

    reset()
    onPostSubmit()
  }

  const handleClose = () => {
    setConfirmOpen(false)
    reset()
    onClose()
  }

  const handlePnSelected = (pn: PnSearch) => {
    setModalPnOpened(false)
    setValue('pn', pn?.pn)
    setValue('designation', pn?.designation)
  }

  useEffect(() => {
    loadOptions()
  }, [])
  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }}>
          <PageHeader
            lang={lang}
            pageTitle={t('9500189')}
            modalEmbedded={true}
            modalOnClose={onClose}
            style={{ top: 0 }}
            localLoading={isLoading}
          />
          <Container style={{ flex: 1 }} fixed>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div style={{ margin: '20px 50px' }}>
                  <Row justify='space-between'>
                    <Col md={5} xs={12}>
                      {displayMode === DisplayModus.CREATE ? (
                        <FormInput
                          name='pn'
                          label={
                            <>
                              {t('9500047')}
                              <FontAwesomeIcon
                                icon={faExternalLinkAlt}
                                className='color_primary'
                                style={{ marginLeft: '1ex' }}
                                onClick={() => setModalPnOpened(true)}
                              />
                            </>
                          }
                          maxLength={40}
                          capitalize
                          error={Boolean(errors.pn)}
                          rules={{ required: true }}
                          onChange={() => resetField('designation')}
                          fullWidth
                        />
                      ) : (
                        <FormInput name='pn' label={t('9500047')} disabled maxLength={40} fullWidth />
                      )}
                    </Col>
                    <Col md={5} xs={12}>
                      <FormSelect
                        name='codeType'
                        label='code type'
                        rules={{ required: true }}
                        options={typeOptions}
                        error={Boolean(errors?.codeType)}
                      />
                    </Col>
                  </Row>
                  <Row justify='space-between'>
                    <Col md={5} xs={12}>
                      <FormInput name='designation' label={t('7000248')} disabled fullWidth />
                    </Col>
                    <Col md={5} xs={12}>
                      <FormNumericInput
                        name='quantite'
                        label={t('7000199')}
                        rules={{ required: true }}
                        minValue={1}
                        maxValue={99999}
                        step={1}
                        slotProps={{
                          htmlInput: {
                            sx: {
                              textAlign: 'right',
                              '&::placeholder': {
                                textAlign: 'right',
                              },
                            },
                          },
                        }}
                      />
                    </Col>
                  </Row>
                </div>
                <Inline
                  align='space-around'
                  style={{
                    padding: '10px',
                    position: 'absolute',
                    bottom: 0,
                    marginTop: '1em',
                    width: '100%',
                  }}
                >
                  <Button onClick={() => (isDirty ? setConfirmOpen(true) : handleClose())} variant='secondary'>
                    {t('8000569')}
                  </Button>
                  <Button type='submit' variant='primary' disabled={isLoading || !isDirty}>
                    {t('9500174')}
                  </Button>
                </Inline>
              </form>
            </FormProvider>
          </Container>
          <DevTool {...methods} />
        </div>
      </Modal>
      <ModalConfirm
        confirmMessage={t('GEN0090')}
        isOpened={confirmOpen}
        onCloseModal={() => setConfirmOpen(false)}
        onSubmit={handleClose}
      />
      <ModalPn
        formTitle={t('9500144')}
        lang={lang}
        isOpened={modalPnOpened}
        onCloseModal={() => setModalPnOpened(false)}
        onPnSelected={handlePnSelected}
        noPnFilter
      />
    </>
  )
}

export default ModalRequiredTools
